<template>
<div>
    <div ref="productSpecGroup" v-if="specGroup.name == 'Color'" class="productDetails-color_swatches">
        <div class="paragraph-default selected-color flex-row">
            <div class="flex-flow-forced" style="white-space: pre;">
                {{ specGroup.name }}: 
                <span id="selectedVariant">{{ specGroup.active_value }}</span>
            </div>
            <div class="status-keys">
                <div v-if="existsSomeUnavailableVariant" class="productDetails-color_container unavailable flex-row-forced status-key">
                    <svg
                        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                        <line x1="10" y1="90" x2="90" y2="10" stroke-width="9"></line>
                        <line x1="10" y1="10" x2="90" y2="90" stroke-width="9"></line>
                    </svg>
                    <div>Not Available</div>
                </div>
                <div v-if="existsSomeOutOfStockVariant" class="productDetails-color_container out-of-stock flex-row-forced status-key">
                    <svg
                        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                        <line x1="10" y1="90" x2="90" y2="10" stroke-width="9" stroke="white"></line>
                        <line x1="10" y1="10" x2="90" y2="90" stroke-width="9" stroke="white"></line>
                    </svg>
                    <div>In Stock Soon</div>
                </div>
            </div>
        </div>
        <div class="productDetails-colors-desktop">
            <div :class="['productDetails-color_container', variantButtonClass(spec)]" v-for="spec in specGroup.values" :key="spec.spec_value_id" :id="`group_${specGroup.name}`" tabindex="0">
                <svg
                    v-if="['unavailable','out-of-stock', 'out-of-stock active'].includes(variantButtonClass(spec))" 
                    class="diagonal-line" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <line x1="10" y1="90" x2="90" y2="10" stroke-width="3" stroke-dasharray="8,4"></line>
                    <line x1="10" y1="10" x2="90" y2="90" stroke-width="3" stroke-dasharray="8,4"></line>
                </svg>
                <img :id="`spec_value_${spec.spec_value_id}`" v-if="spec" :src="optimize(spec.swatch_image)" :alt="spec.spec_value" :title="getTooltip(spec.spec_value, variantButtonClass(spec))" @click="addVariationValue({ ...spec, specGroup })"/>
            </div>
        </div>
        <div class="productDetails-colors-mobile">
            <div :class="['productDetails-color_container', variantButtonClass(spec)]" v-for="spec in specGroup.values" :key="spec.spec_value_id" :id="`group_${specGroup.name}`">
                <svg
                    v-if="['unavailable','out-of-stock', 'out-of-stock active'].includes(variantButtonClass(spec))" 
                    class="diagonal-line" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <line x1="10" y1="90" x2="90" y2="10" stroke-width="3" stroke-dasharray="8,4"></line>
                    <line x1="10" y1="10" x2="90" y2="90" stroke-width="3" stroke-dasharray="8,4"></line>
                </svg>
                <img :id="`spec_value_${spec.spec_value_id}`" v-if="spec" :src="optimize(spec.swatch_image)" :alt="spec.spec_value" :title="getTooltip(spec.spec_value, variantButtonClass(spec))" @click="addVariationValue({ ...spec, specGroup })"/>
            </div>
        </div>
        <!-- <a href="/" class="productDetails-color_link paragraph-details">Explore Tactical BDU Pants in Camo</a> -->
    </div>
    <div v-else class="productDetails-size_container">
        <div class="productDetails-size_title">
            <div class="paragraph-default">{{ specGroup.name }}: <span>{{ specGroup.active_value }}</span></div>
            <a v-if="specGroup.size_chart" class="size-guide paragraph-default" :href="specGroup.size_chart" target="_blank">Size Guide</a>
        </div>
        <div class="productDetails-sizes">
            <div :title="getSpecValueAlt(spec)" :id="`spec_value_${spec.spec_value_id}`" @click="addVariationValue({ ...spec, specGroup })" :class="['productDetails-size_item', variantButtonClass(spec)]" tabindex="0"
                v-for="spec in specGroup.values" :key="spec.spec_value_id">
                <p class="paragraph-details">{{ getSpecValue(spec) }}</p>
            </div>
        </div>
    </div>
</div>
</template>
<script lang="ts" setup>
    import { ref, watch, watchEffect, nextTick, onMounted } from "vue";
    import useImageOptimization from "@/helpers/composables/useImageOptimization";
    const emit = defineEmits(["addVariationValue"]);
    
    let props = defineProps({
        specGroup: {
            type: Object,
            default() {
                return {};
            }
        },
        selectedVariantValues: {
            type: Array,
            default() {
                return [];
            }
        },
        variationValueAvailablity: {
            type: Object,
            default() {
                return {};
            }
        },
        numSpecGroups: {
            type: Number,
            default() {
                return 1;
            }
        },
        defaultValue: {
            type: String,
            default: ""
        },
        color: {
            type: String,
            default: ""
        }
    });

    function variantButtonClass(spec) {
        let isActive = props.selectedVariantValues.some(
            (val) => val.spec_value_id == spec.spec_value_id
        );
        let specsForAvailabilityCheck =  props.variationValueAvailablity?.[spec.spec_value_id.toString()]
        let isAvailable = specsForAvailabilityCheck?.length > 0;
        let inStock = false;
        if(specsForAvailabilityCheck.length > 0){
            let variationCombinationsFound = specsForAvailabilityCheck.filter((variation) => {
                let description = variation.description;
                if(description){

                    let hasOtherSpecs = props.selectedVariantValues.every((selectedSpec) => {
                        if(selectedSpec.specGroup.name == props.specGroup.name) return true;
                        return description[selectedSpec.specGroup.name] == selectedSpec.spec_value;
                    });
                    return description[props.specGroup.name] == spec.spec_value && hasOtherSpecs;
                }
                return false;
            })
            isAvailable = variationCombinationsFound.length > 0 ? true : false;
            inStock = variationCombinationsFound?.some(varCombo=>varCombo.in_stock === 1);
        }

        if(props.specGroup.name == 'Color'){
            if (!isAvailable) return "unavailable";
            else if (!inStock) return isActive ? "out-of-stock active" : "out-of-stock";
            else if (isActive) return "active";
            else return "notActive";    
        }else{
            if (!isAvailable) return "productDetails-size_item-disabled"; 
            else if (isActive) return "active";
            else return "";
        }
    }

    const productSpecGroup = ref(null);
    let existsSomeUnavailableVariant;
    let existsSomeOutOfStockVariant;

    const getTooltip = (value, availability)=>{
        let availabilityText = '';
        if(availability.includes('unavailable')){
            availabilityText = ' - Unavailable';
        }
        else if(availability.includes('out-of-stock')){
            availabilityText = ' - Out of Stock';
        }
        return value + availabilityText;
    }

    function addVariationValue($event) {
        emit("addVariationValue", $event);
    }

    function getSpecValue(spec) {
        return spec.spec_value;
    }

    function getSpecValueAlt(spec) {
        return spec.spec_value;
    }
    watch(() => props.defaultValue, (newValue) => {

        if(newValue == "" || newValue == null) return;

        let isActive = props.selectedVariantValues.some(
            (val) => val.spec_value?.toLowerCase() == newValue?.toLocaleLowerCase()
        );

        if(isActive) return;
        let spec = props.specGroup.values.find(
            (val) => val.spec_value?.toLowerCase() == newValue?.toLocaleLowerCase()
        );
        addVariationValue({
            ...spec,
            specGroup: props.specGroup
            // spec_group_id: props.specGroup.spec_group_id,
            // spec,
            // group: props.specGroup,
            // specGroup: props.specGroup
        });
    }, { immediate: true });
    
    let { optimize } = useImageOptimization();

    onMounted(()=>{
        let searchParams = new URLSearchParams(window.location.search);
        // if searchParams includes value in specGroupName
        if(searchParams.has(props.specGroup.name.toLowerCase())){
            let specValue = searchParams.get(props.specGroup.name.toLowerCase());
            let spec = props.specGroup.values.find(
                (val) => val.spec_value?.toLowerCase() == specValue?.toLocaleLowerCase()
            );
            if(spec){
                addVariationValue({
                    ...spec,
                    specGroup: props.specGroup
                });
            }
        }

        if(props.specGroup.name.toLowerCase() == 'color' && props.color){
            let spec = props.specGroup.values.find(
                (val) => val.spec_value?.toLowerCase() == props.color?.toLocaleLowerCase()
            );
            if(spec){
                addVariationValue({
                    ...spec,
                    specGroup: props.specGroup
                });
            }
        }

        existsSomeUnavailableVariant = ref(false);
        existsSomeOutOfStockVariant = ref(false);

        watchEffect(() => {
            if (props.specGroup.name == 'Color' && productSpecGroup.value && props.selectedVariantValues && props.variationValueAvailablity) {
                nextTick(()=>{
                    existsSomeUnavailableVariant.value = productSpecGroup?.value?.querySelectorAll('.unavailable:not(.status-key)').length;
                    existsSomeOutOfStockVariant.value = !!productSpecGroup?.value?.querySelectorAll('.out-of-stock:not(.status-key)').length;
                })
            }
        });
    })
</script>
